import { useNavigation } from "@react-navigation/native";
import { Settings } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React, { useMemo } from "react";
import { View, XStack, YStack } from "tamagui";
import { useApi } from "../../../core/apis";
import { Body } from "../../../ui/Body";
import { Box } from "../../../ui/Box";
import { Button } from "../../../ui/Button";
import { Group } from "../../../ui/Group";
import { InfoRow, type InfoRowItemProps } from "../../../ui/InfoRow";
import { Label } from "../../../ui/Label";
import type { BusinessCustomerModel } from "../customers/BusinessCustomerModel";
import { BusinessInfoRow } from "../ui/BusinessInfoRow";
import type { BusinessTemplateModel } from "./BusinessTemplateModel";
import { useCreateBusinessPlanFromTemplate } from "./useCreateBusinessPlanFromTemplate";

const TemplateRow: React.FC<{
  customer: BusinessCustomerModel;
  template: BusinessTemplateModel;
}> = observer(({ template, customer }) => {
  const navigation = useNavigation();

  const [isLoading, setIsLoading] = React.useState(false);

  const createPlanFromTemplate = useCreateBusinessPlanFromTemplate();

  const infoRowProps = useMemo<InfoRowItemProps[]>(() => {
    return [
      {
        text: template.uid,
      },
    ];
  }, [template]);

  return (
    <XStack width={"100%"} justifyContent={"space-between"}>
      <YStack key={template.uid} gap={"$small"}>
        <XStack
          gap={"$small"}
          alignItems={"center"}
          cursor={"pointer"}
          onPress={() => {
            navigation.navigate("CustomerAdminTemplate", {
              customerUid: template.data.customerUid,
              templateUid: template.uid,
            });
          }}
        >
          <Body>{template.data.planInfo.title}</Body>
          <Settings size={"$small"} />
        </XStack>
        <InfoRow items={infoRowProps} />
      </YStack>

      <Button
        isLoading={isLoading}
        onPress={async () => {
          setIsLoading(true);
          const businessPlan = await createPlanFromTemplate(
            customer.data,
            template.data.planInfo.title,
            template.uid,
          );

          // Join
          const user = await businessPlan.joinWithCurrentSuperUser();

          // Set as admin at once
          await businessPlan.toggleMemberRole(user.uid, "admin");
          // await businessPlan.toggleGuide(user.uid);

          setIsLoading(false);

          navigation.navigate("BusinessHome", {
            businessUid: businessPlan.uid,
          });
        }}
        type={"primary"}
      >
        USE
      </Button>
    </XStack>
  );
});

export const BusinessAdminTemplatesList = observer(
  ({ customer }: { customer: BusinessCustomerModel }) => {
    const { businessTemplatesApi } = useApi();

    React.useEffect(() => {
      void businessTemplatesApi.getTemplatesForCustomer(customer.uid);
    }, [businessTemplatesApi, customer]);

    const templates = customer.templates;

    return (
      <View>
        <Box gap={"$large"}>
          <BusinessInfoRow
            title={
              "Create templates, and use them to easily create new events."
            }
          />
          <Label>Templates ({templates.length})</Label>

          <Group>
            {templates.map((template) => (
              <TemplateRow
                key={template.uid}
                customer={customer}
                template={template}
              />
            ))}
          </Group>
        </Box>
        <Button
          onPress={() => {
            businessTemplatesApi.add({
              customerUid: customer.uid,
              businessModules: [],
              sections: [],
              schedule: [],
              profileQuestions: [],
              planInfo: {
                title: "My template",
                emoji: "😆",
                backgroundColor: null,
                descriptionYooptaEditor: null,
                coverUrl: null,
              },
              timesUsed: 0,
              settings: {
                useIntroductionText: true,
                useLocale: "en",
                usePlanira: false,
              },
            });
          }}
        >
          Add template
        </Button>
      </View>
    );
  },
);
